import { useState } from "react";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/ui";
import { normaliseKeyword, suggestKeywords } from "../lib/keywords";
import s from "../network.module.css";

export interface KeywordChipsProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** The title to draw suggestions from when the user asks for them. */
  sourceText: string;
  /** The longer description; its phrases count only when they carry a domain word. */
  sourceDetails?: string;
  max?: number;
}

/** Editable keyword chips: type and press Enter or comma to add, Backspace on empty input removes the last one. */
export function KeywordChips({ value, onChange, sourceText, sourceDetails = "", max = 15 }: KeywordChipsProps) {
  const [draft, setDraft] = useState("");
  // Backspace on an empty box first marks the last chip, a second Backspace removes it; typing clears the mark.
  const [armed, setArmed] = useState(false);

  const add = (raw: string) => {
    const k = normaliseKeyword(raw);
    if (!k || value.includes(k) || value.length >= max) return;
    setArmed(false);
    onChange([...value, k]);
  };
  const remove = (k: string) => {
    setArmed(false);
    onChange(value.filter((v) => v !== k));
  };
  const suggest = () => {
    const merged = [...value];
    for (const k of suggestKeywords(sourceText, sourceDetails, max)) if (!merged.includes(k) && merged.length < max) merged.push(k);
    onChange(merged);
  };

  return (
    // The field's label would treat any click in here as a click on its first control, the first chip's remove
    // button, so every click inside the editor cancels that default and focuses the box itself.
    <div className="u-col" style={{ gap: 6 }} onClick={(e) => e.preventDefault()}>
      <div className={s.chipEditor} onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.focus()}>
        {value.map((k, i) => (
          <span key={`${k}-${i}`} className={s.chip} data-armed={armed && i === value.length - 1 ? "true" : undefined} style={armed && i === value.length - 1 ? { outline: "2px solid var(--danger)" } : undefined}>
            {k}
            <button type="button" className={s.chipRemove} aria-label={`Remove keyword ${k}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(k); }}>
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          className={s.chipInput}
          value={draft}
          aria-label="Add keyword"
          placeholder={value.length === 0 ? "Add keywords, press Enter" : ""}
          onChange={(e) => {
            setArmed(false);
            setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
              setDraft("");
            } else if (e.key === "Tab" && draft.trim()) {
              // Tab commits the keyword like Enter does; focus stays here so the next keyword can follow.
              e.preventDefault();
              add(draft);
              setDraft("");
            } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
              // A held Backspace that just erased the typed text must not run on into the chips.
              e.preventDefault();
              if (e.repeat) return;
              if (armed) remove(value[value.length - 1]);
              else setArmed(true);
            } else if (e.key === "Escape") {
              setArmed(false);
            }
          }}
          onBlur={() => {
            if (draft.trim()) {
              add(draft);
              setDraft("");
            }
          }}
        />
      </div>
      <div className="u-row">
        <Button size="sm" variant="ghost" icon={<Sparkles size={14} />} onClick={suggest} disabled={!sourceText.trim() && !sourceDetails.trim()}>
          Suggest keywords
        </Button>
        <span className={`${s.small} ${s.muted}`}>
          {value.length}/{max}. Keywords drive matching; add synonyms.
        </span>
      </div>
    </div>
  );
}
